'use client';

import { ImageCarousel } from '@/components/sections/about/team/image-carousel/image-carousel';
import H3 from '@/components/typography/h3';
import P from '@/components/typography/p';
import { Teamleden } from '@/payload-types';
import { Dot, X } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TeamProps {
    members: Teamleden[];
}

export default function Team({ members }: TeamProps) {
    const { t } = useTranslation();

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    return (
        <>
            <div className="relative py-15 lg:pt-[6.5rem]">
                <ImageCarousel
                    className="-ml-[12rem] w-[calc(100%+12rem)]"
                    height={{
                        mobile: 288,
                        desktop: 670,
                    }}
                    slides={[
                        {
                            src: '/images/about/carousel/about-us-1.jpg',
                        },
                        {
                            src: '/images/about/carousel/about-us-2.jpg',
                        },
                        {
                            src: '/images/about/carousel/about-us-3.jpg',
                        },
                        {
                            src: '/images/about/carousel/about-us-4.jpg',
                        },
                        {
                            src: '/images/about/carousel/about-us-5.jpg',
                        },
                    ]}
                />
            </div>
            {/* Desktop view */}
            <div className="hidden xl:block">
                <div className="border-primary relative flex flex-col items-center justify-center gap-5 border-b px-6 pt-[5rem] pb-[6.7rem]">
                    <div className="flex w-full gap-20 2xl:gap-3">
                        <div className="relative w-5/12">
                            <P variant="large" className="ml-[10rem] w-[425px]">
                                {t('team.text')}
                            </P>
                        </div>
                        <div className="w-6/12">
                            <div className="flex w-full flex-col gap-3">
                                {members.map((member, index) => (
                                    <div
                                        key={index}
                                        className="group border-primary relative flex w-fit items-center pb-[1px] hover:border-b hover:pb-0"
                                    >
                                        <H3 variant="notItalic">
                                            {member.naam}
                                        </H3>
                                        <Dot
                                            className="mx-2.5"
                                            strokeWidth={3}
                                        />
                                        <P
                                            variant="large"
                                            className="text-[24px]!"
                                        >
                                            {member.functie}
                                        </P>

                                        {/* Hover image */}
                                        <div className="absolute right-[-9rem] z-20 hidden group-hover:block">
                                            <Image
                                                src={
                                                    typeof member.foto ===
                                                    'string'
                                                        ? '/placeholder.svg'
                                                        : member.foto?.url ||
                                                          '/placeholder.svg'
                                                }
                                                alt="Team Image"
                                                className="mr-4 h-[75px] w-[75px] rounded-xl object-cover lg:h-[200px] lg:w-[200px]"
                                                width={400}
                                                height={400}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile view */}
            <div className="relative block xl:hidden">
                <div className="border-primary relative flex flex-col items-center justify-center gap-5 border-b px-6 pt-3 pb-[3.5rem]">
                    <P variant="large">{t('team.text')}</P>
                    <div className="mt-8 flex w-full flex-col gap-4">
                        {members.map((member, index) => (
                            <div key={index} className="relative flex flex-col">
                                <button
                                    onClick={() =>
                                        setSelectedIndex(
                                            selectedIndex === index
                                                ? null
                                                : index,
                                        )
                                    }
                                    className="w-fit text-left"
                                >
                                    <H3
                                        variant="notItalic"
                                        className="text-start underline"
                                    >
                                        {member.naam}
                                    </H3>
                                </button>
                                <P variant="large" className="text-[20px]">
                                    {member.functie}
                                </P>

                                {selectedIndex === index && (
                                    <div className="absolute top-[-3rem] left-[65%] z-10 sm:top-[-5rem] sm:left-[25%] md:left-[35%]">
                                        <div className="relative h-[130px] w-[130px] sm:h-[230px] sm:w-[230px]">
                                            <button
                                                className="absolute top-2 right-2 z-20 rounded-full bg-white p-1 text-black"
                                                onClick={() =>
                                                    setSelectedIndex(null)
                                                }
                                            >
                                                <X strokeWidth={1} />
                                            </button>
                                            <Image
                                                src={
                                                    typeof member.foto ===
                                                    'string'
                                                        ? '/placeholder.svg'
                                                        : member.foto?.url ||
                                                          '/placeholder.svg'
                                                }
                                                alt="Team Image"
                                                className="rounded-xl object-cover"
                                                width={250}
                                                height={250}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}
